import { forwardRef, useCallback, useEffect, useRef, useState } from "react";

import classNames from "classnames";
import type React from "react";

import { Separator } from "./Separator";

import style from "./ShareList.module.css";

interface ShareListProps {
  children: React.ReactNode;
  className?: string;
}

export const ShareList = forwardRef<HTMLDivElement, ShareListProps>(
  ({ children, className }, ref) => {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const topSentinelRef = useRef<HTMLDivElement>(null);
    const bottomSentinelRef = useRef<HTMLDivElement>(null);

    const [isAtTop, setIsAtTop] = useState(true);
    const [isAtBottom, setIsAtBottom] = useState(false);
    const [hasOverflow, setHasOverflow] = useState(false);

    const checkOverflow = useCallback(() => {
      const container = containerRef.current;
      if (!container) return;

      const hasOverflow = container.scrollHeight > container.clientHeight;
      setHasOverflow(hasOverflow);
    }, []);

    useEffect(() => {
      const container = containerRef.current;
      const topSentinel = topSentinelRef.current;
      const bottomSentinel = bottomSentinelRef.current;

      if (!container || !topSentinel || !bottomSentinel) return;

      const intersectionObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.target === topSentinel) {
              setIsAtTop(entry.isIntersecting);
            } else if (entry.target === bottomSentinel) {
              setIsAtBottom(entry.isIntersecting);
            }
          });
        },
        {
          root: container,
          rootMargin: "0px",
          threshold: 0,
        }
      );

      intersectionObserver.observe(topSentinel);
      intersectionObserver.observe(bottomSentinel);

      return () => {
        intersectionObserver.disconnect();
      };
    }, []);

    useEffect(() => {
      const container = containerRef.current;
      if (!container) return;

      const resizeObserver = new ResizeObserver(() => {
        checkOverflow();
      });

      resizeObserver.observe(container);

      checkOverflow();

      return () => {
        resizeObserver.disconnect();
      };
    }, [checkOverflow]);

    useEffect(() => {
      const animationFrameId = requestAnimationFrame(() => {
        checkOverflow();
      });

      return () => cancelAnimationFrame(animationFrameId);
    }, [children, checkOverflow]);

    const mergedRef = useCallback(
      (node: HTMLDivElement | null) => {
        containerRef.current = node;
        if (typeof ref === "function") {
          ref(node);
        } else if (ref) {
          ref.current = node;
        }
      },
      [ref]
    );

    const showTopSeparator = hasOverflow && !isAtTop;
    const showBottomSeparator = hasOverflow && !isAtBottom;

    return (
      <div className={className} ref={mergedRef}>
        <Separator
          className={classNames(style.overflowSeparatorTop, {
            [style.visible]: showTopSeparator,
          })}
        />
        <div ref={topSentinelRef} className={style.sentinel} />

        {children}
        <div ref={bottomSentinelRef} className={style.sentinel} />

        <Separator
          className={classNames(style.overflowSeparatorBottom, {
            [style.visible]: showBottomSeparator,
          })}
        />
      </div>
    );
  }
);

ShareList.displayName = "ShareList";
